import { useQuery } from '@tanstack/react-query';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {
  SourcingPerformanceCriteria,
  SourcingPerformanceStatus,
  SourcingService,
} from 'api-clients/monolith';
import clsx from 'clsx';
import _round from 'lodash/round';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { usePerformanceStatus } from '@/components/base/performance-status-pill/use-performance-status';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Typography } from '@/components/ui/typography';
import { queryKeys } from '@/query-keys';

interface PerformanceStatusPillProps {
  status: SourcingPerformanceStatus;
  /** When set, tooltip metrics load from the performance_status_metrics API on open. */
  openingSlug?: string;
  /** Omit for current goal — the server resolves it. Pass only when a past goal is pinned in opening details. */
  hiringGoalId?: string;
  showAggregateData?: boolean;
  hireActual?: number | null;
  applicantActual?: number | null;
  /** Inline metrics when the parent already has them (stages / at-risk list). */
  criteria?: SourcingPerformanceCriteria;
  expected?: number | null;
  actual?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  duration?: number;
  manageHiringGoalLink?: string;
}

export function PerformanceStatusPill({
  status,
  openingSlug,
  hiringGoalId,
  showAggregateData,
  hireActual,
  applicantActual,
  criteria: criteriaProp,
  expected: expectedProp,
  actual: actualProp,
  startDate,
  endDate,
  duration,
  manageHiringGoalLink,
}: PerformanceStatusPillProps) {
  const [open, setOpen] = useState(false);
  const shouldFetchMetrics = Boolean(openingSlug);

  const { data, isFetching, isError } = useQuery({
    queryKey: [
      ...queryKeys.dashboard.performanceStatusMetrics,
      openingSlug,
      hiringGoalId,
      showAggregateData,
    ],
    queryFn: () =>
      SourcingService.getInternalApiSourcingOpeningsPerformanceStatusMetrics(
        openingSlug!,
        hiringGoalId,
        showAggregateData,
      ),
    enabled: open && shouldFetchMetrics,
  });

  const metrics = data?.performance_status_metrics;
  // Skeleton only while a fetch is in flight with no metrics yet.
  const isLoadingTooltip =
    shouldFetchMetrics && open && isFetching && !metrics;
  const showMetricsError =
    shouldFetchMetrics && open && isError && !metrics && !isFetching;
  const criteria = shouldFetchMetrics
    ? (metrics?.criteria ?? null)
    : (criteriaProp ?? null);
  const expected = shouldFetchMetrics
    ? ((criteria === 'hire'
        ? metrics?.expected_hires_count
        : metrics?.expected_applicants_count) ?? null)
    : (expectedProp ?? null);
  const actual = shouldFetchMetrics
    ? ((criteria === 'hire' ? hireActual : applicantActual) ?? null)
    : (actualProp ?? null);

  const { bgColor, icon, tooltip, title } = usePerformanceStatus({
    status,
    criteria,
    expected: _round(expected ?? 0),
    actual: _round(actual ?? 0),
    startDate,
    endDate,
    duration,
    manageHiringGoalLink,
  });

  if (!status) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <TooltipPrimitive.Root open={open} onOpenChange={setOpen}>
        <TooltipTrigger asChild>
          <div
            className={clsx(
              'flex h-6 w-fit items-center justify-center gap-1 rounded-full pr-3 pl-2 whitespace-nowrap',
              bgColor,
            )}
          >
            <img src={icon} alt="Status" />
            <Typography variant="small1" className="!text-fx-neutral-600">
              {title}
            </Typography>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" align="center">
          {showMetricsError ? (
            <FormattedMessage
              defaultMessage="Couldn't load performance metrics."
              description="Tooltip fallback when the opening performance metrics request fails"
            />
          ) : isLoadingTooltip ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-3 w-48" />
              <Skeleton className="h-3 w-40" />
            </div>
          ) : (
            tooltip
          )}
        </TooltipContent>
      </TooltipPrimitive.Root>
    </TooltipProvider>
  );
}

PerformanceStatusPill.Skeleton = function StatusPillSkeleton() {
  return <Skeleton className="h-6 w-18 !rounded-full" />;
};
